// `useSubmitJob()` — TanStack mutation backing the SubmitForm.
//
// On success: invalidate ['jobs'] so the list shows the new row when the
// user navigates back, and let the route component handle navigation via
// useNavigate (it owns the routing context).
//
// Caller pattern:
//   const navigate = useNavigate();
//   const mutation = useSubmitJob();
//   mutation.mutate(
//     { address: trimmed },
//     { onSuccess: (data) => navigate(`/jobs/${data.job_id}`, { replace: true }) },
//   );

import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';
import { submitJob } from '@/lib/api';
import type { SubmitJobResponse } from '@/types/db';

export interface SubmitJobVariables {
  address: string;
}

export function useSubmitJob(): UseMutationResult<
  SubmitJobResponse,
  Error,
  SubmitJobVariables
> {
  const queryClient = useQueryClient();
  return useMutation<SubmitJobResponse, Error, SubmitJobVariables>({
    mutationFn: ({ address }) => submitJob(address),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['jobs'] });
    },
  });
}
